import { friendlyDbError } from '../utils/friendlyDbError';
import { create } from 'zustand';
import type { ServiceCostTemplate, ServiceCostTemplateFormData } from '../types';
import { ServiceCostTemplateService } from '../services/serviceCostTemplateService';

interface ServiceCostTemplateStore {
  templates: ServiceCostTemplate[];
  loading: boolean;
  error: string | null;
  costCategories: any[];

  // Actions
  fetchTemplates: (serviceId: string) => Promise<void>;
  createTemplate: (serviceId: string, data: ServiceCostTemplateFormData) => Promise<ServiceCostTemplate>;
  updateTemplate: (id: string, data: Partial<ServiceCostTemplateFormData>) => Promise<ServiceCostTemplate>;
  deleteTemplate: (id: string) => Promise<void>;
  getTemplatesByServiceId: (serviceId: string) => ServiceCostTemplate[];
  fetchCostCategories: () => Promise<void>;
  clearTemplates: () => void;
  clearTemplatesForService: (serviceId: string) => void;
}

export const useServiceCostTemplateStore = create<ServiceCostTemplateStore>((set, get) => ({
  templates: [],
  loading: false,
  error: null,
  costCategories: [],

  fetchTemplates: async (serviceId: string) => {
    set({ loading: true, error: null });
    
    try {
      const templates = await ServiceCostTemplateService.getByServiceId(serviceId);
      
      set(state => ({
        templates: [
          // Remove any existing templates for this service
          ...state.templates.filter(t => t.service_id !== serviceId),
          // Add the new templates
          ...templates
        ],
        loading: false
      }));
    } catch (error) {
      console.error('Error fetching service cost templates:', error);
      set({ 
        error: friendlyDbError(error) || 'Failed to fetch templates',
        loading: false 
      });
    }
  },

  createTemplate: async (serviceId: string, data: ServiceCostTemplateFormData) => {
    set({ loading: true, error: null });
    
    try {
      const template = await ServiceCostTemplateService.create(serviceId, data);
      
      set(state => ({
        templates: [...state.templates, template],
        loading: false
      }));
      
      return template;
    } catch (error) {
      console.error('Error creating service cost template:', error);
      set({ 
        error: friendlyDbError(error) || 'Failed to create template',
        loading: false 
      });
      throw error;
    }
  },

  updateTemplate: async (id: string, data: Partial<ServiceCostTemplateFormData>) => {
    set({ loading: true, error: null });
    
    try {
      const updatedTemplate = await ServiceCostTemplateService.update(id, data);
      
      set(state => ({
        templates: state.templates.map(t => 
          t.id === id ? updatedTemplate : t
        ),
        loading: false
      }));
      
      return updatedTemplate;
    } catch (error) {
      console.error('Error updating service cost template:', error);
      set({ 
        error: friendlyDbError(error) || 'Failed to update template',
        loading: false 
      });
      throw error;
    }
  },

  deleteTemplate: async (id: string) => {
    set({ loading: true, error: null });
    
    try {
      await ServiceCostTemplateService.delete(id);
      
      set(state => ({
        templates: state.templates.filter(t => t.id !== id),
        loading: false
      }));
    } catch (error) {
      console.error('Error deleting service cost template:', error);
      set({ 
        error: friendlyDbError(error) || 'Failed to delete template',
        loading: false 
      });
      throw error;
    }
  },

  getTemplatesByServiceId: (serviceId: string) => {
    return get().templates.filter(template => template.service_id === serviceId);
  },

  fetchCostCategories: async () => {
    try {
      const categories = await ServiceCostTemplateService.getCostCategories();
      set({ costCategories: categories });
    } catch (error) {
      console.error('Error fetching cost categories:', error);
    }
  },

  clearTemplates: () => {
    set({ templates: [], error: null });
  },

  clearTemplatesForService: (serviceId: string) => {
    console.log('🧹 Clearing cost templates for service:', serviceId);
    set(state => ({
      templates: state.templates.filter(template => template.service_id !== serviceId)
    }));
    console.log('✅ Cost templates cleared for service');
  }
}));